import React from 'react';
import PropTypes from 'prop-types';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import axios from '../../axiosClient.js';
const customers = [
  ['bswh', 'BSWH'],
  ['chs', 'CHS'],
  ['chi', 'CHI'],
  ['dignity', 'Dignity'],
  ['duke', 'Duke'],
  ['johns-hopkins', 'Johns Hopkins'],
  ['medline', 'Medline'],
  ['morris', 'Morris'],
  ['santa-clara', 'Santa Clara'],
  ['ssm', 'SSM'],
  ['st-bernards', 'St Bernards / Miss County'],
  ['upmc', 'UPMC'],
  ['wvuhs', 'WVUHS'],
];

const AR_MAILBOX_BASE_KEY = 'parse-invoice.ar-mailbox-base';

function normalizeArMailboxBase(value) {
  const trimmed = value.trim();
  if (!trimmed) throw new Error('Paste the Gmail delegated mailbox URL from the AR account menu.');

  let url;
  try {
    url = new URL(trimmed);
  } catch {
    throw new Error('Paste a valid Gmail delegated mailbox URL from the AR account menu.');
  }

  if (url.origin !== 'https://mail.google.com') {
    throw new Error('Paste the full Gmail delegated mailbox URL for the AR account.');
  }

  const match = /^\/mail\/u\/\d+\/d\/[^/?#]+\/?$/.test(url.pathname);
  if (!match) {
    throw new Error(
      'Paste the delegated mailbox URL from Gmail’s account menu; it should link to the AR mailbox root.'
    );
  }

  return `${url.origin}${url.pathname.replace(/\/?$/, '/')}`;
}

function configureArMailbox() {
  const value = window.prompt(
    'Open Gmail → profile picture → ar@tzmedical.com (Delegated). Then paste the AR mailbox URL from that browser tab.'
  );

  if (!value?.trim()) return null;

  const base = normalizeArMailboxBase(value);
  localStorage.setItem(AR_MAILBOX_BASE_KEY, base);

  return base;
}

function getArMailboxBase() {
  const saved = localStorage.getItem(AR_MAILBOX_BASE_KEY);

  if (saved) {
    try {
      return normalizeArMailboxBase(saved);
    } catch {
      localStorage.removeItem(AR_MAILBOX_BASE_KEY);
    }
  }

  return configureArMailbox();
}

// BLOCK PI.INBOX.UI.0 — Saved sender settings and server-side inbox processing.
function ImportRow({ customerType, label, initialSender }) {
  const [sender, setSender] = React.useState(initialSender);
  const [saved, setSaved] = React.useState(initialSender);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [result, setResult] = React.useState(null);
  const [jobs, setJobs] = React.useState([]);
  const submitting = React.useRef(false);
  async function history() {
    const { data } = await axios.get(`/api/parse-invoice/inbox/history/${customerType}`);
    setJobs(data.jobs);
  }
  async function act(action) {
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError('');
    try {
      if (action === 'save') {
        await axios.put('/api/parse-invoice/inbox/settings', { customerType, sender });
        setSaved(sender.trim().toLowerCase());
        setSender(sender.trim().toLowerCase());
        setResult(null);
        const { data } = await axios.post(
          '/api/parse-invoice/inbox/run',
          { customerType },
          { timeout: 25 * 60000 }
        );
        setResult(data);
      } else if (action === 'search') {
        setResult(null);
        const { data } = await axios.post(
          '/api/parse-invoice/inbox/search',
          { customerType },
          { timeout: 25 * 60000 }
        );
        setResult(data);
      }
      await history();
    } catch (e) {
      setError(
        e.response?.data?.error ||
          'Request did not complete. Refresh history before searching again.'
      );
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }

  async function openMail(job) {
    setError('');

    let base;
    try {
      base = getArMailboxBase();
    } catch (e) {
      setError(e.message);
      return;
    }

    if (!base) return;

    const tab = window.open('about:blank', '_blank');
    if (!tab) {
      setError('Allow pop-ups for this site to open source emails.');
      return;
    }

    tab.opener = null;

    try {
      const { data } = await axios.get(
        `/api/parse-invoice/inbox/job-search/${encodeURIComponent(customerType)}/${encodeURIComponent(job.id)}`
      );

      if (
        typeof data?.searchQuery !== 'string' ||
        !data.searchQuery.startsWith('in:anywhere rfc822msgid:')
      ) {
        throw new Error('The server returned an invalid email search.');
      }

      const url = `${base}#search/${encodeURIComponent(data.searchQuery)}`;

      if (!tab.closed) {
        tab.location.replace(url);
      }
    } catch (e) {
      if (!tab.closed) tab.close();
      setError(e.response?.data?.error || e.message || 'Unable to open the source email.');
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
        <Typography sx={{ minWidth: 140 }}>{label}</Typography>
        <TextField
          fullWidth
          label="Remittance sender email"
          type="email"
          value={sender}
          onChange={(e) => setSender(e.target.value)}
          disabled={busy}
        />
        <Button onClick={() => act('save')} disabled={busy || !sender.trim() || sender === saved}>
          Save
        </Button>
        <Button
          variant="contained"
          onClick={() => act('search')}
          disabled={busy || !saved || sender !== saved}
          sx={{ minWidth: 140 }}
        >
          Search Inbox
        </Button>
        <Button onClick={() => act('history')} disabled={busy}>
          History
        </Button>
        {busy && <CircularProgress size={22} />}
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      {result && (
        <Alert
          severity={
            result.failed || result.review || (result.summary && result.summary.failed)
              ? 'warning'
              : 'success'
          }
          sx={{ mt: 2 }}
        >
          {result.summary
            ? `${result.summary.successful} successful; ${result.summary.partial} partial; ${result.summary.failed} failed.`
            : `${result.submitted} CSVs submitted to OCR; ${result.skipped} previously claimed attachments skipped; ${result.failed} parsing failures; ${result.review} email submissions need review.`}
        </Alert>
      )}
      {jobs.map((job) => (
        <Typography key={job.id} variant="body2" sx={{ mt: 1 }}>
          {job.emailId || job.rfc822MessageId ? (
            <>
              <Button
                size="small"
                onClick={() => openMail(job)}
                sx={{
                  p: 0,
                  minWidth: 0,
                  textTransform: 'none',
                  textDecoration: 'underline',
                  verticalAlign: 'baseline',
                }}
              >
                {job.filename}
              </Button>{' '}
              <span>Open source email</span>
            </>
          ) : (
            job.filename
          )}{' '}
          — {job.status} — {new Date(job.updatedAt).toLocaleString()}
          {job.error ? ` — ${job.error}` : ''}
        </Typography>
      ))}
    </Paper>
  );
}
ImportRow.propTypes = {
  customerType: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  initialSender: PropTypes.string.isRequired,
};
export const PAGE_PERMISSION_KEY = 'parse-invoice';
export default function ParseInvoicePage() {
  const [settings, setSettings] = React.useState(null);
  const [error, setError] = React.useState('');
  React.useEffect(() => {
    document.title = 'Parse Invoice';
    axios
      .get('/api/parse-invoice/inbox/settings')
      .then(({ data }) => {
        setSettings(data.settings);
      })
      .catch(() =>
        setError('Unable to load inbox settings. Check permissions and database setup.')
      );
  }, []);
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Parse Invoice
      </Typography>
      <Typography sx={{ mb: 2 }}>
        Search ar@tzmedical.com for remittance attachments and submit CSVs to OCR@tzmedical.com.
        Save each sender before searching. First search covers seven days; subsequent searches
        resume from the saved checkpoint.
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {settings && (
        <Stack spacing={2}>
          {customers.map(([customerType, label]) => (
            <ImportRow
              key={customerType}
              customerType={customerType}
              label={label}
              initialSender={settings.find((s) => s.customerType === customerType)?.sender || ''}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
